import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';
import moment from 'moment-timezone';

import Img from '../Img';
import BallType from '../text/BallType';
import InvitedPlayer from '../../pages/EventProfiles/EventProfilePageV3/PageComponents/InvitedPlayer';
import InfoButton from '../InfoButton';

import { getTeamPaymentTypeOfDivision, hasAdminAddPaymentRequiredInvitation, hasPlayerInvitationAndRegDateOpen, isDoublesDivision, isPickleBallSportTypeEvent, renderDivisionPrice } from '../../utils/events';
import { getDescriptionByTypeId, getLabelByTypeId } from '../../utils/types';
import { dateFormat, divisionGenderTypes, eventGenderTypeIds, registrationStateTypeIds, regStepIds, teamPaymentTypeIds, utrTypeIds, utrTypes, ANNUAL_POWER_ONLY_LABEL } from '../../utils/eventConstants';
import { getClickAttrs } from '../Analytics/gtag';
import { eventDivisionAgeSliderDictionary, maxUtrValue, maxUtrpValue, minUtrValue, minUtrpValue, ratingTypes } from '../../utils/constants';
import { errorMessage } from '../../utils/displayMessageConstants';
import { addTeammateToDivison, setDeclineInvitationData } from '../../actions/event-profile-actions';
import { InvitationTypes } from '../../pages/EventProfiles/EventProfilePageV3/PageComponents/PlayerPendingInvitations/utils';

import styles from './eventRegForm.scss';

const DivisionSelectionV2 = (props) => {
    const {
        division,
        event,
        selected,
        disabled,
        hideCheckbox,
        handleClick,
        shouldShowGenderInSubTitle = false,
        shouldDisplayTeamPaymentType = false,
        shouldDisplayAgeRestrictionText = false,
        shouldDisplayRangeRestrictionText = false,
        canChangePartner = false,
        toStep,
        declinePartnerInvitation,
        clubVerificationFee,
        isAdminAddedPaymentRequired,
        hideDeclineButton,
        userIsRegisteredToDivision,
        changePartnerFlow,
    } = props;

    // remapping non standard prop names // TODO: fix api prop names
    if (!division.player2 && division.partnerDetails) {
        division.player2 = division.partnerDetails;
    }
    if (!division.player2 && division.needsPartner) {
        division.player2 = { needsPartner: true };
    }
    if (!division.id && division.divisionId) {
        division.id = division.divisionId;
    }
    if (!division.player2 && !division.partnerDetails && division.playerNameFromInvite) {
        const playerName = division.playerNameFromInvite.split(' ');
        division.player2 = {
            firstName: playerName[0],
            lastName: playerName[1],
        };
    }

    const { registerAsIndividual, isWaitlistLocked, allowWaitlist } = division;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const auth = useSelector(({ auth }) => auth);
    const dispatch = useDispatch();
    const {
        types: {
            tms: {
                genderTypes,
                division: {
                    teamPaymentTypes,
                },
            },
        },
    } = coreUI;

    const isSanctioned = event.utrType?.value === utrTypes.SANCTIONED || event.utrTypeId === utrTypeIds.SANCTIONED;
    const isDoubles = isDoublesDivision(division) || changePartnerFlow;
    const onCheckChange = () => {
        if (!disabled) {
            handleClick(division);
            if (isDoubles && canChangePartner && !division.player2 && !selected && !registerAsIndividual) {
                addPartner();
            }
            if (registerAsIndividual) {
                const player2 = {
                    name: 'Need Partner',
                    firstName: 'Need',
                    lastName: 'Partner',
                    needsPartner: true,
                };
                dispatch(addTeammateToDivison({ divisionId: division.id, player2 }));
            }
        }
    };
    const teamPaymentType = getTeamPaymentTypeOfDivision(division, teamPaymentTypes);
    const shouldShowPriceDescription = !!event.allowOffPlatformPayments;

    const subTitle = useMemo(() => {
        let value;
        const isV2Event = !!event.utrTypeId;
        if (shouldShowGenderInSubTitle) {
            if (isV2Event) {
                const isMixedOrCoedType = division.genderId === eventGenderTypeIds.ALL || division.genderId === eventGenderTypeIds.MIXED;
                const areDivisionAndUserSameGender = getLabelByTypeId(genderTypes, division.genderId)?.toLowerCase() !== auth.user.gender?.toLowerCase();
                if (!isMixedOrCoedType && areDivisionAndUserSameGender) {
                    value = `${getDescriptionByTypeId(genderTypes, division.genderId)} Only`;
                }
            } else if (
                division.gender?.label?.toLowerCase() !== auth.user.gender?.toLowerCase() &&
                (division.gender?.value !== divisionGenderTypes.ALL || division.gender?.value !== divisionGenderTypes.MIXED)
            ) {
                value = `${division.gender?.description} Only`;
            }
        }

        if (!value && division.annualPoweredOnly) {
            value = ANNUAL_POWER_ONLY_LABEL;
        }

        return value;
    }, [shouldShowGenderInSubTitle, division, event?.utrTypeId]);

    const ageRestrictionText = useMemo(() => {
        const underMinAgeForEvent = eventDivisionAgeSliderDictionary[0];
        const overMaxAgeForEvent = eventDivisionAgeSliderDictionary[Object.keys(eventDivisionAgeSliderDictionary).length - 1];

        if (division.restrictAge) {
            if (division.minAge === underMinAgeForEvent && division.maxAge === overMaxAgeForEvent) {
                return null;
            }
            if (division.minAge === division.maxAge) {
                return `Age ${division.minAge} Only`;
            }
            if (division.minAge === underMinAgeForEvent && division.maxAge !== overMaxAgeForEvent) {
                return `Ages ${division.maxAge} & Under`;
            }
            if (division.maxAge === overMaxAgeForEvent && division.minAge !== underMinAgeForEvent) {
                return `Ages ${division.minAge} & Over`;
            }
            if (division.minAge === underMinAgeForEvent && division.maxAge === overMaxAgeForEvent) {
                return `All Ages`;
            }
            return `Ages ${division.minAge} - ${division.maxAge}`;
        }
        return null;
    }, [division]);

    const rangeRestrictionText = useMemo(() => {
        const isPickleBallEvent = isPickleBallSportTypeEvent(event?.sportTypeId);
        const minRatingForEvent = isPickleBallEvent ? minUtrpValue : minUtrValue;
        const maxRatingForEvent = isPickleBallEvent ? maxUtrpValue : maxUtrValue;
        const ratingName = isPickleBallEvent ? ratingTypes.pickleball : ratingTypes.tennis;

        if (division.restrictByRating) {
            if (division.minUtr === minRatingForEvent && division.maxUtr === maxRatingForEvent) {
                return null;
            }
            if (division.minUtr !== minRatingForEvent && division.maxUtr === maxRatingForEvent) {
                return `${ratingName} ${division.minUtr} +`;
            }
            if (division.minUtr === minRatingForEvent && division.maxUtr !== maxRatingForEvent) {
                return `${ratingName} < ${division.maxUtr}`;
            }
            if (division.minUtr !== minRatingForEvent && division.maxUtr !== maxRatingForEvent) {
                if (division.minUtr === division.maxUtr) {
                    return `${ratingName} ${division.minUtr} Only`;
                }

                return `${ratingName} ${division.minUtr} - ${division.maxUtr}`;
            }
        }

        return null;
    }, [division]);

    const changePartner = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();

        if (division.isInvited) {
            declinePartnerInvitation({
                division,
            });
        } else {
            addPartner(true);
        }
    };

    const addPartner = (clearPlayer2) => {
        const playerIsInNeedPartnerList = division.player2?.needsPartner;
        if (clearPlayer2) {
            dispatch(addTeammateToDivison({ divisionId: division.id, player2: null }));
        }
        toStep(regStepIds.TEAMMATE, division.id, changePartnerFlow, playerIsInNeedPartnerList);
    };

    const isDivisionFull = () => {
        if (isDoubles) {
            const registeredCount = division.isAcceptance ? Math.round(division.registeredCount / 2) : division.nonAcceptanceMainCount;
            return (!!division.maxTeams && registeredCount >= division.maxTeams) || division.maxTeams === 0;
        }
        return (!!division.maxPlayers && division.registeredCount >= division.maxPlayers) || division.maxPlayers === 0;
    };

    const showSpecificDivisionDate = (division) => {
        return division.specificDivisionDateEnabled && division.specificDivisionDateUtc;
    };

    const onDeclineAdminAddedPlayer = () => {
        props.handleCloseClick();
        dispatch(setDeclineInvitationData({
            divisionId: division.id,
            type: InvitationTypes.ADMIN_ADD,
        }));
    };

    const getChangePartnerButtonLabel = () => {
        const showAlternatePreRegCopy = event.registrationStateId === registrationStateTypeIds.PRE_REGISTRATION && event.hasEligiblePlayersList;
        if (division.isInvited) {
            if (showAlternatePreRegCopy) {
                return 'Decline invite';
            }
            return 'Remove partner';
        }
        return 'Change partner';
    };

    if (division.isEventEntryFeeDivision) {
        return null;
    }
    const isPendingInvitation = (event?.currentMember?.registeredDivisions || []).some(div => div.id === division.id && (hasPlayerInvitationAndRegDateOpen(div, division) || hasAdminAddPaymentRequiredInvitation(div)));
    const hideChangePartner = isAdminAddedPaymentRequired && !division.player2?.needsPartner;

    return (
        <div className={styles.divisionSelection}>
            <div
                className={`
                    radio-button
                    radio-button-xl
                    noselect
                    ${selected && 'checked'}
                    ${disabled && 'disabled disabled-divison-selection'}
                `}
            >
                <input
                    id={`division${division.id}`}
                    type="checkbox"
                    checked={selected}
                    disabled={disabled}
                    onChange={onCheckChange}
                    data-testid={`event-reg.division-checkbox-${division.id}`}
                    {...getClickAttrs({
                        category: 'Event Profile CTA',
                        location: 'Register toggle division',
                        context: `Register toggle division - ${division.id}`,
                    })}
                />
                <label
                    htmlFor={`division${division.id}`}
                >
                    {!hideCheckbox && (
                        <div className="radio-button-check">
                            {selected && <Img src="/img/icons/checkmark-icon.svg" />}
                        </div>
                    )}
                    <div className={`radio-button-label mw0`}>
                        <div className="d-flex justify-content-between">
                            <div className="flex-1 mw0 text-break mr8">
                                <div>
                                    {division.name || division.divisionName}
                                </div>
                                <div className="radio-button-description fs12 text-bold">{division?.divisionCategoryModel?.label}</div>
                                {!!division.ballType && (
                                    <div
                                        className="radio-button-description fs12 text-bold"
                                    >
                                        <BallType ballType={division.ballType} />
                                    </div>
                                )}
                                {shouldDisplayTeamPaymentType && teamPaymentType && !registerAsIndividual && (
                                    <div className="radio-button-description fs12 text-bold">{teamPaymentType}</div>
                                )}
                                {registerAsIndividual && (
                                    <div className="radio-button-description fs12 text-bold">Individual player only</div>
                                )}
                                {shouldDisplayAgeRestrictionText && ageRestrictionText && (
                                    <div className="radio-button-description fs12 text-bold">{ageRestrictionText}</div>
                                )}
                                {shouldDisplayRangeRestrictionText && rangeRestrictionText && (
                                    <div className="radio-button-description fs12 text-bold">{rangeRestrictionText}</div>
                                )}
                                {showSpecificDivisionDate(division) && !props.preventDivisionDateRegistrationConflicts && (
                                    <div className="radio-button-description fs12 text-bold">
                                        {moment.utc(division.specificDivisionDateUtc).tz(event?.eventSchedule?.timeZone?.value).format(dateFormat)}
                                    </div>
                                )}
                                {subTitle && (
                                <div
                                    className="radio-button-description fs12 text-bold"
                                >
                                    {subTitle}
                                </div>
                                )}
                            </div>
                            <div className="d-flex flex-col justify-content-between align-items-center">
                                <div className="price text-right">
                                    {renderDivisionPrice(
                                        division,
                                        event.allowOffPlatformPayments,
                                        isSanctioned,
                                        event.workflowVersion,
                                        auth.user,
                                        clubVerificationFee,
                                    )}
                                </div>
                                {shouldShowPriceDescription && (
                                    <div className="text-right text-teal description">
                                        Fee Due in Your Country`s Currency
                                    </div>
                                )}
                            </div>
                        </div>
                        {division.description && (
                            <div className="radio-button-description">
                                {division.description}
                            </div>
                        )}
                        {isDoubles && division.player2 && (
                            <div className={styles.partnerSection}>
                                {division.player2.needsPartner ? (
                                    <div className="fs14 text-medium">You are on a Need Partner list for this division.</div>
                                ) : (
                                    <InvitedPlayer
                                        player={division.player2}
                                        showDescription={!division.player2.needsPartner}
                                        sportTypeId={event?.sportTypeId}
                                    />
                                )}
                                {!hideChangePartner && canChangePartner && !registerAsIndividual && !division.player2.needsPartner && (
                                    <button
                                        type="button"
                                        className={`flex-grow-4 btn btn-sm btn-block btn-default mt8 text-uppercase`}
                                        onClick={changePartner}
                                        disabled={disabled}
                                        data-testid="event-reg.change-partner-btn"
                                    >
                                        {getChangePartnerButtonLabel()}
                                    </button>
                                )}
                                {canChangePartner && division.player2.needsPartner && (
                                    <button
                                        type="button"
                                        className={`flex-grow-4 btn btn-sm btn-block btn-primary mt8 text-uppercase`}
                                        onClick={() => addPartner()}
                                        data-testid="event-reg.add-partner-btn-change"
                                    >
                                        Add a Partner
                                    </button>
                                )}
                            </div>
                        )}
                        {isDoubles && canChangePartner && !division.player2 && !registerAsIndividual && (
                            <div className="text-right mt16 w-100 d-flex justify-content-between align-items-center">
                                <button
                                    type="button"
                                    className={`flex-grow-4 btn btn-sm btn-block ${division.player2 ? 'btn-default' : 'btn-primary'} text-uppercase`}
                                    onClick={addPartner}
                                    disabled={disabled || !selected}
                                    data-testid="event-reg.add-partner-btn-new"
                                >
                                    Add a Partner
                                </button>
                                {division.teamPaymentTypeId !== teamPaymentTypeIds.PAY_SEPARATELY && (
                                    <InfoButton
                                        anchorClass={`${styles.radioDoubleInfobutton} flex-grow-3 ml-3`}
                                        hoverText="You must register as a team. If you don't have a teammate and want to play doubles or dingles, please coordinate with the event organizer to find a teammate and then register."
                                    />
                                )}
                                {division.teamPaymentTypeId === teamPaymentTypeIds.PAY_SEPARATELY && (
                                    <InfoButton
                                        anchorClass={`${styles.radioDoubleInfobutton} flex-grow-3 ml-3`}
                                        hoverText={(
                                            <ul>
                                                <li>
                                                    Both you and your partner need to register and pay any applicable fees separately.
                                                </li>
                                                <li>
                                                    After you complete your registration, your partner needs to register and pay through the same event page. When your partner starts their registration, they will find your name already selected in the division.
                                                </li>
                                                <li>
                                                    Your team&apos;s registration will be finalized only when both partners have registered and paid.
                                                </li>
                                            </ul>
                                        )}
                                    />
                                )}
                            </div>
                        )}
                        {isAdminAddedPaymentRequired && !hideDeclineButton && (
                            <button
                                type="button"
                                className={`flex-grow-4 btn btn-sm btn-block btn-default mt8 text-uppercase`}
                                onClick={onDeclineAdminAddedPlayer}
                                data-testid="event-reg.decline-invite-btn"
                            >
                                Decline invite
                            </button>
                        )}
                        {((!isAdminAddedPaymentRequired && !userIsRegisteredToDivision && isDivisionFull() && !isPendingInvitation) || isWaitlistLocked) && (
                            (allowWaitlist || isWaitlistLocked) ? (
                                <div className={'mt8'}>
                                    <div className={'fs14 fw600 text-warning mb4'} data-testid="event-reg.waitlist-active">
                                        Waitlist only: Register to Join
                                    </div>
                                    <ul className={'fs12 pl16'}>
                                        <li>
                                            Confirm reg. status before making travel plans.
                                        </li>
                                        <li>
                                            Full refund for divisions you do not get into.
                                        </li>
                                        <li>
                                            Contact the event provider with any questions.
                                        </li>
                                    </ul>
                                </div>
                            ) : (
                                <div className={'text-small text-error description mt8'} data-testid="event-reg.division-full-message">
                                    {errorMessage.cappedDivisionFullMessage}
                                </div>
                            )
                        )}
                        {changePartnerFlow && (
                            <button
                                type="button"
                                className={`flex-grow-4 btn btn-sm btn-block ${division.player2?.firstName ? 'btn-default' : 'btn-primary'} text-uppercase mt16`}
                                onClick={addPartner}
                            >
                                {division.player2?.firstName ? 'Change Partner' : 'Add a Partner'}
                            </button>
                        )}
                    </div>
                </label>
            </div>
        </div>
    );
};

DivisionSelectionV2.propTypes = {
    division: PropTypes.objectOf(any).isRequired,
    event: PropTypes.objectOf(any).isRequired,
    selected: PropTypes.bool,
    disabled: PropTypes.bool,
    handleClick: PropTypes.func,
    shouldShowGenderInSubTitle: PropTypes.bool,
    shouldDisplayTeamPaymentType: PropTypes.bool,
    shouldDisplayAgeRestrictionText: PropTypes.bool,
    shouldDisplayRangeRestrictionText: PropTypes.bool,
    canChangePartner: PropTypes.bool,
    toStep: PropTypes.func,
    declinePartnerInvitation: PropTypes.func,
    clubVerificationFee: PropTypes.number,
    preventDivisionDateRegistrationConflicts: PropTypes.bool,
    handleCloseClick: PropTypes.func,
    isAdminAddedPaymentRequired: PropTypes.bool,
    hideDeclineButton: PropTypes.bool,
    userIsRegisteredToDivision: PropTypes.bool,
    changePartnerFlow: PropTypes.bool,
    hideCheckbox: PropTypes.bool,
};

DivisionSelectionV2.defaultProps = {
    selected: false,
    disabled: false,
    shouldShowGenderInSubTitle: false,
    shouldDisplayTeamPaymentType: false,
    shouldDisplayAgeRestrictionText: false,
    shouldDisplayRangeRestrictionText: false,
    canChangePartner: false,
    handleClick: () => { },
    toStep: () => { },
    declinePartnerInvitation: () => { },
    clubVerificationFee: 0,
    preventDivisionDateRegistrationConflicts: false,
    handleCloseClick: () => { },
    isAdminAddedPaymentRequired: false,
    hideDeclineButton: false,
    userIsRegisteredToDivision: false,
    changePartnerFlow: false,
    hideCheckbox: false,
};

export default DivisionSelectionV2;
